
// client/src/hooks/useUnreadMessages.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { getUnreadCount } from '../services/messagesApi';

/**
 * useUnreadMessages Hook
 * 
 * Tracks unread message count for the currently logged-in user.
 * Features:
 * - Polls backend every 30 seconds for updated count
 * - Listens for custom events (new-message, message-read, unread:refresh)
 * - Only counts messages for the specified tenantId and userId
 * - Handles errors gracefully without breaking the UI
 * 
 * @param {number|null} tenantId - Current tenant ID
 * @param {number|null} userId - Current user ID
 * @returns {Object} { unreadCount, loading, error, refetch }
 */
export function useUnreadMessages(tenantId, userId) {
  // =====================================================
  // State Management
  // =====================================================
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // =====================================================
  // Refs to track mount state and intervals
  // =====================================================
  const isMounted = useRef(true);
  const pollIntervalRef = useRef(null);
  const fetchInProgressRef = useRef(false);

  // =====================================================
  // Fetch Function (memoized to prevent unnecessary re-renders)
  // =====================================================
  const fetchUnreadCount = useCallback(async () => {
    // Don't fetch if:
    // - Component is unmounted
    // - Missing required IDs
    // - Fetch already in progress
    if (!isMounted.current || !tenantId || !userId || fetchInProgressRef.current) {
      return;
    }

    fetchInProgressRef.current = true;

    try {
      // Call the API service to get unread count
      const count = await getUnreadCount({ tenantId, userId });
      
      // Only update state if component is still mounted
      if (isMounted.current) {
        setUnreadCount(Number(count) || 0);
        setError(null);
      }
    } catch (err) {
      // Log error but don't break the UI
      console.error('[useUnreadMessages] Failed to fetch unread count:', err);
      
      if (isMounted.current) {
        setError(err);
        // Keep the last known count instead of resetting to 0
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
      fetchInProgressRef.current = false;
    }
  }, [tenantId, userId]);

  // =====================================================
  // Initial Fetch + Polling Setup
  // =====================================================
  useEffect(() => {
    // Reset state when tenantId or userId changes
    setLoading(true);
    setError(null);

    // Skip if missing required IDs
    if (!tenantId || !userId) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    // Initial fetch
    fetchUnreadCount();

    // Setup polling interval (every 30 seconds)
    pollIntervalRef.current = setInterval(fetchUnreadCount, 30000);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [tenantId, userId, fetchUnreadCount]);

  // =====================================================
  // Event Listeners for Real-time Updates
  // =====================================================
  useEffect(() => {
    // Only setup listeners if we have valid IDs
    if (!tenantId || !userId) {
      return;
    }

    /**
     * Event handler for various unread count triggers:
     * - 'new-message': When a new message is received
     * - 'message-read': When a message is marked as read
     * - 'unread:refresh': Manual refresh trigger from other components
     */
    const handleRefresh = () => {
      if (isMounted.current) {
        fetchUnreadCount();
      }
    };

    // Register event listeners
    window.addEventListener('new-message', handleRefresh);
    window.addEventListener('message-read', handleRefresh);
    window.addEventListener('unread:refresh', handleRefresh);

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('new-message', handleRefresh);
      window.removeEventListener('message-read', handleRefresh);
      window.removeEventListener('unread:refresh', handleRefresh);
    };
  }, [tenantId, userId, fetchUnreadCount]);

  // =====================================================
  // Track Mount State
  // =====================================================
  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
    };
  }, []);

  // =====================================================
  // Return Hook Values
  // =====================================================
  return {
    unreadCount,      // Current unread message count (number)
    loading,          // Initial loading state (boolean)
    error,            // Any error that occurred (Error | null)
    refetch: fetchUnreadCount  // Manual refetch function
  };
}

export default useUnreadMessages;


























































